import {
  SxProps,
  Tooltip,
  Typography,
  TypographyPropsVariantOverrides,
  TypographyVariant,
} from "@mui/material";
import { ReactElement } from "react";

import { palette } from "~frontend/theme";

import { FlexBox } from "../FlexBox";

export type Format = "number" | "percent" | "usd";

type Size = "xlarge" | "large" | "small";

export const formatters: {
  [format in Format]: (value: number, option?: number) => string;
} = {
  percent: (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  },
  number: (value: number) => {
    return value.toLocaleString();
  },
  usd: (value: number, fractionDigits = 0) => {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: fractionDigits,
    });
  },
};

const styleMap: Record<
  Size,
  {
    fontSize?: string;
    fontWeight?: string;
    variant?: keyof TypographyPropsVariantOverrides | TypographyVariant;
    labelVariant: keyof TypographyPropsVariantOverrides | TypographyVariant;
    paddingTop: string;
    margin: string;
  }
> = {
  xlarge: {
    fontSize: "60px",
    fontWeight: "bold",
    labelVariant: "h2Caps",
    paddingTop: "10px",
    margin: "10px 30px",
  },
  large: {
    variant: "h0Bold",
    paddingTop: "10px",
    labelVariant: "body2Bold",
    margin: "10px 30px",
  },
  small: {
    variant: "h1Bold",
    paddingTop: "5px",
    labelVariant: "body3Bold",
    margin: "5px 10px",
  },
};

export default function NumberStat({
  format = "number",
  value,
  size = "large",
  visible = true,
  children,
  disabled = false,
  disabledMessage,
  sx = {},
}: {
  format?: Format;
  value: number;
  size?: Size;
  visible?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  disabledMessage?: string;
  sx?: SxProps;
}): ReactElement {
  return visible ? (
    <Tooltip title={disabledMessage ?? ""}>
      <div>
        <FlexBox
          flexDirection="column"
          sx={{
            maxWidth: "400px",
            margin: styleMap[size].margin,
            ...sx,
          }}
        >
          <Typography
            variant={styleMap[size].variant}
            sx={{
              paddingTop: styleMap[size].paddingTop,
              textAlign: "center",
              fontSize: styleMap[size].fontSize,
              fontWeight: styleMap[size].fontWeight,
              color: disabled ? palette.darkGray : palette.navy,
            }}
          >
            {disabled ? "-" : formatters[format](value)}
          </Typography>

          <Typography
            variant={styleMap[size].labelVariant}
            color={disabled ? palette.darkGray : palette.navy}
            sx={{ textAlign: "center" }}
          >
            {children}
          </Typography>
        </FlexBox>
      </div>
    </Tooltip>
  ) : (
    <></>
  );
}
